import React from 'react';
import { EllipsisVerticalIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import { Link } from 'react-router-dom';
import CardContent from '@dashboard/components/CardContent';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import StatusBadge from '@dashboard/components/StatusBadge';
import { Button as ShadcnButton } from '@dashboard/components/ui/button';
import { formatPrice } from '@/dashboard/services/formatting';
import Tooltip, { InfoTooltip } from '@/dashboard/components/Tooltip';
import checkIsEventSoldOut from '@dashboard/services/event/checkIsEventSoldOut';
import checkIsTicketGroupSoldOut from '@repo/checkout/lib/checkIsTicketGroupSoldOut';
import TicketTypeSaleDate from './TicketTypeSaleDate';
import checkIsTicketTypeScheduled from '@repo/checkout/lib/checkIsTicketTypeScheduled';
import checkIsTicketGroupScheduled from '@repo/checkout/lib/checkIsTicketGroupScheduled';
import checkIsTicketTypeOnSale from '@dashboard/services/event/checkIsTicketTypeOnSale';
import checkIsTicketGroupOnSale from '@dashboard/services/event/checkIsTicketGroupOnSale';
import TicketGroupEventOverviewBadges from '../../PageEdit/scenes/PageEvent/components/EventTicketTypesForm/components/TicketGroupEventOverviewBadges';
import {
  TicketGroupAvailabilityStatus,
  TicketTypeAvailabilityStatus,
} from '@/dashboard/enums/tickets';
import checkIsTicketTypeSoldOut from '@repo/checkout/lib/checkIsTicketTypeSoldOut';
import { shouldShowTicketSaleDates } from '@repo/checkout/lib/ticketAvailabilityHelpers';
import checkIsTicketTypeNotOnSale from '@repo/checkout/lib/checkIsTicketTypeNotOnSale';
import checkIsTicketGroupNotOnSale from '@repo/checkout/lib/checkIsTicketGroupNotOnSale';
import getPageTheme from '@repo/checkout/lib/theming/getPageTheme';
import getTicketPriceHeadline from '@dashboard/services/event/getTicketPriceHeadline';

function EventTicketSalesTableRow({ ticket, pageId, isGroup, ticketGroup, page, showOutstanding }) {
  const queryKey = isGroup ? 'ticketGroup' : 'ticketType';

  const viewPath = `/pages/${pageId}/attendees?${queryKey}=${ticket._id}`;
  const editPath = `/pages/${pageId}/customize/event/ticket-types`;

  const isTicketGroupSoldOut = checkIsTicketGroupSoldOut(ticketGroup);
  const isTicketTypeSoldOut = checkIsTicketTypeSoldOut(ticket);
  const isSoldOut = checkIsEventSoldOut(page) || isTicketGroupSoldOut || isTicketTypeSoldOut;

  const isCanceled = page.eventDetails?.status === 'canceled';

  const isTicketTypeUnavailable =
    ticket.availabilityStatus === TicketTypeAvailabilityStatus.UNAVAILABLE;
  const isTicketGroupUnavailable =
    ticketGroup?.availabilityStatus === TicketGroupAvailabilityStatus.UNAVAILABLE;
  const isUnavailable = isTicketGroupUnavailable || isTicketTypeUnavailable;

  const allTicketTypes = page.ticketGroups.map(group => group.ticketTypes).flat();

  const ticketTypeScheduleResult = checkIsTicketTypeScheduled({
    ticketType: ticket,
    allTicketTypes,
  });
  const ticketGroupScheduleResult = checkIsTicketGroupScheduled({
    ticketGroup,
    allTicketGroups: page.ticketGroups,
  });

  const isScheduled = ticketTypeScheduleResult.isScheduled || ticketGroupScheduleResult.isScheduled;
  const hasInvalidReference =
    ticketTypeScheduleResult.hasInvalidReference || ticketGroupScheduleResult.hasInvalidReference;

  const isTicketGroupOnSale = checkIsTicketGroupOnSale({
    page,
    ticketGroup,
    allTicketGroups: page.ticketGroups,
  });

  const isTicketTypeOnSale = checkIsTicketTypeOnSale({
    page,
    ticketType: ticket,
    allTicketTypes,
  });

  const isOnSale = isTicketTypeOnSale && isTicketGroupOnSale;

  const isTicketTypeNotOnSale = checkIsTicketTypeNotOnSale({
    ticketType: ticket,
    isCanceled,
    hasInvalidReference: ticketTypeScheduleResult.hasInvalidReference,
    isOnSale: isTicketTypeOnSale,
    isScheduled: ticketTypeScheduleResult.isScheduled,
  });

  const isGroupNotOnSale = checkIsTicketGroupNotOnSale({
    ticketGroup,
    isCanceled,
    hasInvalidReference: ticketGroupScheduleResult.hasInvalidReference,
    isOnSale: isTicketGroupOnSale,
    isScheduled: ticketGroupScheduleResult.isScheduled,
  });

  const isNotOnSale = isTicketTypeNotOnSale || isGroupNotOnSale;

  const showTicketSaleDates = shouldShowTicketSaleDates(
    ticket,
    ticketGroup,
    isCanceled,
    isSoldOut,
    isUnavailable,
    isOnSale,
  );

  const isEventSoldOut = checkIsEventSoldOut(page);

  const showTicketTypeHiddenBadge =
    ticket.status === 'enabled' &&
    (ticket.hidden === true ||
      ticketGroup.hidden === true ||
      (ticket.hideWhenNotOnSale && isTicketTypeNotOnSale) ||
      (ticketGroup.hideWhenNotOnSale && isNotOnSale) ||
      (ticket.hideWhenScheduled && ticketTypeScheduleResult.isScheduled) ||
      (ticketGroup.hideWhenScheduled && isScheduled) ||
      (ticket.hideWhenUnavailable && isTicketTypeUnavailable) ||
      (ticketGroup.hideWhenUnavailable && isTicketGroupUnavailable) ||
      (ticket.hideWhenSoldOut && isTicketTypeSoldOut) ||
      (ticketGroup.hideWhenSoldOut && isTicketGroupSoldOut));

  const theme = getPageTheme(page);

  return (
    <CardListItem
      href={`/pages/${pageId}/attendees?${queryKey}=${ticket._id}`}
      className="mb-1 border rounded-sm border-gray-200 dark:border-border text-sm"
    >
      <CardListItemElement size="fill" align="right">
        <div className="gap-y-1">
          {isGroup && <div className="text-sm text-gray-500 dark:text-muted-foreground">Group</div>}
          <div className="flex items-center gap-2">
            <div className="font-medium">{ticket.name}</div>
          </div>

          {!isGroup && (
            <>
              <div className="text-sm">
                {getTicketPriceHeadline({
                  ticketType: ticket,
                  locale: theme.locale,
                  currency: page.eventDetails?.currency || ticket.currency,
                })}
              </div>

              {showTicketSaleDates && (
                <div className="text-gray-700 dark:text-muted-foreground text-sm">
                  <TicketTypeSaleDate
                    page={page}
                    ticketType={ticket}
                    ticketGroup={ticketGroup}
                    allTicketTypes={ticketGroup?.ticketTypes ?? []}
                    allTicketGroups={page.ticketGroups ?? []}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </CardListItemElement>

      <CardListItemElement size="large" className="!py-0">
        <div className="font-medium">
          {formatPrice({
            locale: theme.locale,
            amount: ticket.revenue.totalRevenue || 0,
            currency: page.eventDetails?.currency || ticket.currency,
          })}
        </div>
      </CardListItemElement>
      {showOutstanding && (
        <CardListItemElement size="large" className="!py-0">
          <div className="font-medium">
            {formatPrice({
              locale: theme.locale,
              amount: ticket.revenue.totalOutstanding || 0,
              currency: page.eventDetails?.currency || ticket.currency,
            })}
          </div>
        </CardListItemElement>
      )}
      <CardListItemElement size="medium">
        <span>
          {ticket.quantitySold ?? 0}
          {ticket.capacity != null && `/${ticket.capacity}`}
        </span>
      </CardListItemElement>
      <CardListItemElement size="large">
        <div>
          <div className="font-medium">
            {ticket.checkedInCount || 0}/{ticket.quantitySold}
          </div>
        </div>
      </CardListItemElement>
      <CardListItemElement size="large" className="flex justify-between">
        <div className="flex items-center">
          <div className="flex flex-col items-start">
            {isGroup ? (
              <TicketGroupEventOverviewBadges
                page={page}
                ticketGroup={ticketGroup}
                allTicketGroups={page.ticketGroups}
              />
            ) : (
              <>
                {ticket.status === 'disabled' ? (
                  <Tooltip label="Disabled tickets will not be shown to customers.">
                    <StatusBadge status="disabled" label="Disabled" />
                  </Tooltip>
                ) : ticket.status === 'archived' ? (
                  <Tooltip label="This ticket is shown as archived to customers.">
                    <StatusBadge status="archived" label="Archived" />
                  </Tooltip>
                ) : isCanceled ? (
                  <Tooltip label="This event has been canceled.">
                    <StatusBadge status="canceled" label="Canceled" />
                  </Tooltip>
                ) : isEventSoldOut ? (
                  <Tooltip label="This ticket no longer has availability.">
                    <StatusBadge status="sold_out" label="Sold out" />
                  </Tooltip>
                ) : ticketGroup.availabilityStatus === TicketGroupAvailabilityStatus.UNAVAILABLE ? (
                  <Tooltip label="This ticket is shown as unavailable to customers.">
                    <StatusBadge status="unavailable" label="Unavailable" />
                  </Tooltip>
                ) : isGroupNotOnSale ? (
                  <Tooltip label="This ticket is currently not on sale..">
                    <StatusBadge status="sale_ended" label="Not on sale" />
                  </Tooltip>
                ) : isSoldOut ? (
                  <Tooltip label="This ticket no longer has availability.">
                    <StatusBadge status="sold_out" label="Sold out" />
                  </Tooltip>
                ) : isUnavailable ? (
                  <Tooltip label="This ticket is shown as unavailable to customers.">
                    <StatusBadge status="unavailable" label="Unavailable" />
                  </Tooltip>
                ) : isNotOnSale || hasInvalidReference || isTicketTypeNotOnSale ? (
                  <Tooltip label="This ticket is currently not on sale..">
                    <StatusBadge status="sale_ended" label="Not on sale" />
                  </Tooltip>
                ) : isOnSale ? (
                  <Tooltip label="This ticket is on sale.">
                    <StatusBadge status="on_sale" label="On sale" />
                  </Tooltip>
                ) : isScheduled ? (
                  <Tooltip label="This ticket is scheduled to go on sale.">
                    <StatusBadge status="scheduled" label="On sale soon" />
                  </Tooltip>
                ) : (
                  <Tooltip label="This ticket is on sale.">
                    <StatusBadge status="on_sale" label="On sale" />
                  </Tooltip>
                )}

                {showTicketTypeHiddenBadge && (
                  <Tooltip label="This ticket is hidden.">
                    <StatusBadge status="hidden" label="Hidden" />
                  </Tooltip>
                )}
              </>
            )}
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <ShadcnButton variant="ghost" size="icon" aria-label="More options" className="mr-2">
              <EllipsisVerticalIcon className="size-4" />
            </ShadcnButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={editPath}>Edit</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={viewPath}>View guests</Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardListItemElement>
    </CardListItem>
  );
}

function EventTicketSales({ ticketGroups, page }) {
  const pageWithTicketGroups = { ...page, ticketGroups };
  const showOutstanding = (ticketGroups || []).some(ticketGroup =>
    ticketGroup.ticketTypes.some(ticket => ticket.revenue?.totalOutstanding > 0),
  );
  const getGroupStats = ticketGroup => {
    const totalPrice = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalOriginalPrice || 0);
    }, 0);

    const totalDiscountAmount = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalDiscountAmount || 0);
    }, 0);

    const totalFeeAmount = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalFeeAmount || 0);
    }, 0);

    const totalTaxAmount = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalTaxAmount || 0);
    }, 0);

    const totalCouponAmount = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalCouponAmount || 0);
    }, 0);

    const totalComplimentaryAmount = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalComplimentaryAmount || 0);
    }, 0);

    const totalRevenue = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalRevenue || 0);
    }, 0);

    const totalOutstanding = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalOutstanding || 0);
    }, 0);

    const totalSold = ticketGroup.ticketTypes.reduce((sum, ticket) => sum + ticket.quantitySold, 0);

    const totalCapacity = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.capacity || 0);
    }, 0);

    const totalRemaining = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.capacity != null ? ticket.capacity - ticket.quantitySold : 0);
    }, 0);

    const totalCheckedIn = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.checkedInCount || 0);
    }, 0);

    const totalOriginalPrice = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalOriginalPrice || 0);
    }, 0);

    return {
      totalOriginalPrice,
      totalRevenue,
      totalOutstanding,
      totalPrice,
      totalDiscountAmount,
      totalFeeAmount,
      totalTaxAmount,
      totalCouponAmount,
      totalComplimentaryAmount,
      totalSold,
      totalCapacity,
      totalRemaining,
      totalCheckedIn,
    };
  };

  return (
    <div className="space-y-6">
      {(ticketGroups || [])
        .filter(ticketGroup => ticketGroup.ticketTypes.length > 0)
        .map((ticketGroup, index) => {
          const groupStats = getGroupStats(ticketGroup);

          return (
            <div key={ticketGroup._id}>
              <CardContent className="overflow-auto">
                <CardListItem
                  variant="header"
                  className="!bg-white dark:!bg-background !border-0 pb-2 !mt-2 text-sm"
                >
                  <CardListItemElement size="fill" align="left" className="!py-0">
                    {index === 0 && (
                      <div className="font-semibold text-gray-900 dark:text-foreground text-base !-ml-4 !pl-0">
                        Ticket sales
                      </div>
                    )}
                  </CardListItemElement>

                  <CardListItemElement size="large" className="!py-0">
                    <div className="font-medium text-gray-900 dark:text-foreground">
                      Revenue
                      <Tooltip
                        label="Gross revenue after discounts, fees, and taxes — all applied proportionally,
                      before refunds"
                      >
                        <span type="button" className="inline-block align-sub ml-1">
                          <InformationCircleIcon className="w-4 h-4 text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground transition-colors" />
                        </span>
                      </Tooltip>
                    </div>
                  </CardListItemElement>

                  {showOutstanding && (
                    <CardListItemElement size="large" className="!py-0">
                      <div className="font-medium text-gray-900 dark:text-foreground">
                        Outstanding
                        <InfoTooltip>
                          Outstanding revenue after discounts, fees and taxes - all applied
                          proportionally before refunds
                        </InfoTooltip>
                      </div>
                    </CardListItemElement>
                  )}

                  <CardListItemElement size="medium" className="!py-0">
                    <div className="font-medium text-gray-900 dark:text-foreground flex items-center gap-1">
                      Sold
                    </div>
                  </CardListItemElement>
                  <CardListItemElement size="large" className="!py-0">
                    <div className="font-medium text-gray-900 dark:text-foreground">Checked in</div>
                  </CardListItemElement>
                  <CardListItemElement size="large" className="!py-0">
                    <div className="font-medium text-gray-900 dark:text-foreground flex items-center gap-1">
                      Status
                    </div>
                  </CardListItemElement>
                </CardListItem>

                <EventTicketSalesTableRow
                  ticket={{
                    ...ticketGroup,
                    name: ticketGroup.name,
                    revenue: {
                      ...groupStats,
                    },
                    totalRevenue: groupStats.totalRevenue,
                    status: ticketGroup.status,
                    quantitySold: groupStats.totalSold,
                    checkedInCount: groupStats.totalCheckedIn,
                    capacity: ticketGroup.capacity,
                    hidden: ticketGroup.hidden,
                  }}
                  isGroup={true}
                  showOutstanding={showOutstanding}
                  pageId={page._id}
                  ticketGroup={ticketGroup}
                  page={pageWithTicketGroups}
                />

                {ticketGroup.ticketTypes.map(ticket => (
                  <EventTicketSalesTableRow
                    key={ticket._id}
                    ticket={{
                      ...ticket,
                      status: ticketGroup.status === 'disabled' ? 'disabled' : ticket.status,
                    }}
                    showOutstanding={showOutstanding}
                    pageId={page._id}
                    ticketGroup={ticketGroup}
                    page={pageWithTicketGroups}
                  />
                ))}
              </CardContent>
            </div>
          );
        })}
    </div>
  );
}

export default EventTicketSales;
